<template>
	<div ref="positioner" class="cloud-positioner" :class="{top}">
		<Teleport to="#clouds">
			<div ref="cloud" class="in-place-cloud" :style="'--trigger-left:'+triggerLeft+'px;--trigger-top:'+triggerTop+'px;--trigger-width:'+triggerWidth+'px;--trigger-height:'+triggerHeight+'px'" :class="{active: active && showCloud, top, measuring: active && !showCloud}">
				<div class="inner" ref="inner-cloud">
					<slot></slot>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<script>
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	name: "AppInPlaceCloud",
	mixins: [WindowWidth],
	emits: ["close-cloud"],
	props: {
		active: {
			type: Boolean,
			default: false
		},
		listenScroll: {
			type: Boolean,
			default: true
		},
		forceTop: {
			type: Boolean,
			default: false
		},
	},
	data() {
		return {
			triggerLeft: 0,
			triggerTop: 0,
			triggerWidth: 0,
			triggerHeight: 0,
			autoTop: false,
			showCloud: false,
			windowHeight: window.innerHeight,
		}
	},
	computed: {
		top() {
			return this.forceTop || this.autoTop;
		}
	},
	methods: {
		onHeightChange() {
			this.windowHeight = window.innerHeight;
		},
		positionCloud() {
			if(!this.$refs['positioner']) {
				return;
			}
			// Get parent element (the actual trigger button/element)
			const parentElement = this.$refs['positioner'].parentElement;
			if(!parentElement) {
				return;
			}

			const rect = parentElement.getBoundingClientRect();
			this.triggerLeft = rect.left + rect.width / 2;
			this.triggerTop = rect.top + window.scrollY;
			this.triggerWidth = rect.width;
			this.triggerHeight = rect.height;

			// First, try bottom position to measure
			this.autoTop = false;

			// Auto-detect best position after positioning
			// Use double nextTick to ensure cloud is fully rendered with content
			this.$nextTick(() => {
				this.$nextTick(() => {
					this.detectBestPosition();
				});
			});
		},
		detectBestPosition() {
			if(!this.$refs['cloud'] || this.forceTop) {
				this.showCloud = true;
				return;
			}

			const cloudHeight = this.$refs['cloud'].offsetHeight;
			const viewportHeight = window.innerHeight;
			const buffer = 20; // Extra space buffer

			// Calculate trigger position relative to viewport (not page)
			// Use parent element (same as in positionCloud)
			const parentElement = this.$refs['positioner'].parentElement;
			if(!parentElement) {
				this.showCloud = true;
				return;
			}
			const triggerRect = parentElement.getBoundingClientRect();
			const triggerBottomInViewport = triggerRect.bottom;
			const triggerTopInViewport = triggerRect.top;

			// Calculate available space in visible viewport
			const spaceBelow = viewportHeight - triggerBottomInViewport;
			const spaceAbove = triggerTopInViewport;

			// If not enough space below but enough space above, position on top
			if(spaceBelow < cloudHeight + buffer && spaceAbove > cloudHeight + buffer) {
				this.autoTop = true;
				// Wait for DOM update with new position before showing
				this.$nextTick(() => {
					this.showCloud = true;
				});
			} else {
				this.autoTop = false;
				this.showCloud = true;
			}
		},
		scrollListener(e) {
			if(this.$refs['inner-cloud']) {
				let maxIterations = 10;
				let currentIterations = 0;
				let element = e.target;
				while (element.parentElement && currentIterations <= maxIterations) {
					if(element === this.$refs['inner-cloud']) { return }
					element = element.parentElement;
					currentIterations++;
				}
				this.positionCloud();
				this.$emit("close-cloud");
			}
		},
	},
	created() {
		if(!document.getElementById("clouds")) {
			let popups = document.createElement("div");
			popups.id = "clouds";
			document.body.appendChild(popups);
		}
	},
	mounted() {
		window.addEventListener('resize', this.onHeightChange);
	},
	beforeUnmount() {
		if(this.listenScroll) {
			window.removeEventListener('scroll', this.scrollListener);
		}
		window.removeEventListener('resize', this.onHeightChange);
	},
	watch: {
		active(newStateActive) {
			if(newStateActive) {
				this.showCloud = false;
			}
			this.$nextTick(this.positionCloud);
			if(this.listenScroll) {
				if(newStateActive) {
					window.addEventListener('scroll', this.scrollListener, true);
				} else {
					window.removeEventListener('scroll', this.scrollListener, true);
				}
			}
		},
		windowWidth() {
			if(this.active) {
				this.$emit("close-cloud");
			}
		},
		windowHeight() {
			if(this.active) {
				this.$emit("close-cloud");
			}
		}
	}
}
</script>

<style lang="scss" scoped>
:root {
	--trigger-left: 0px;
	--trigger-top: 0px;
	--trigger-width: 0px;
	--trigger-height: 0px;
}
.cloud-positioner {
	position: absolute;
	bottom: 0;
	left: 50%;

	&.top {
		top: 0;
		bottom: auto;
	}
}
.in-place-cloud {
	position: absolute;
	top: calc(var(--trigger-top) + var(--trigger-height) + 5px);
	transform: translateX(-50%) translateY(0);
	transform-origin: top;
	width: max-content;
	max-width: 450px;
	left: clamp(
		calc(10px + 450px / 2),
		var(--trigger-left),
		calc(100vw - 10px - 450px / 2)
	);

	background-color: var(--color-bg-primary-1);
	border-radius: 13px;
	transition: visibility 0.3s $transition 50ms, opacity 0.3s $transition 50ms, transform .3s $transition 50ms;
	z-index: 10000;
	box-shadow: -1px -1px 2px 0px rgba(7, 16, 32, 0.3) inset, 1px 1px 2px 0px hsla(0, 0%, 100%, 0.4) inset, 1px 4px 20px -9px rgb(1 10 21 / 70%);

	@media (max-width: 520px) {
		width: calc(100vw - 20px);
		max-width: calc(100vw - 20px);
		left: 50vw !important;
		transform: translateX(-50%) translateY(0);
	}

	.inner {
		position: relative;
		padding: 12px;

		&:deep(*) {
			font-size: 14px;
			line-height: 16px;
		}

		&:after {
			content: ' ';
			position: absolute;
			width: 15px;
			height: 15px;
			left: calc(50% + var(--trigger-left) - clamp(
				calc(10px + 450px / 2),
				var(--trigger-left),
				calc(100vw - 10px - 450px / 2)
			) - 7.5px);
			border-radius: 5px 0 5px 0;
			right: auto;
			top: -6.5px;
			bottom: auto;
			transition: left .1s $transition;
			background: linear-gradient(317deg, var(--color-bg-primary-1) 50% 0%, var(--color-bg-primary-1) 60%);
			border-right: 1px solid rgba(255, 255, 255, 0.8);
			transform: rotate(225deg);
			filter: blur(0.5px);

			@media (max-width: 520px) {
				left: calc(var(--trigger-left) - 50vw + 50% - 7.5px);
			}
		}
	}

	&:not(.active) {
		opacity: 0;
		visibility: hidden;
		pointer-events: none;
		transform: translateX(-50%) translateY(-10px);
	}

	&.measuring {
		opacity: 0 !important;
		pointer-events: none;
		transition: none;
	}

	&.top {
		top: calc(var(--trigger-top) - 10px);
		transform: translateX(-50%) translateY(-100%);
		transform-origin: bottom;

		&:not(.active) {
			transform: translateX(-50%) translateY(calc(-100% + 10px));
		}

		@media (max-width: 520px) {
			transform: translateX(-50%) translateY(-100%);

			&:not(.active) {
				transform: translateX(-50%) translateY(calc(-100% + 10px));
			}
		}

		.inner:after {
			bottom: -8.5px;
			top: auto;
			transform: rotate(45deg);
		}
	}

}

</style>